import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchMC, FetchMCOnboardings, FetchMCOnboardingsCounts } from 'src/constants/actionTypes'
import {
  mcOnboardingPageSizeOptions,
  mcOnboardingInitialSortFields,
  mcOnboardingColumns,
} from 'src/constants/mc-onboarding/mcOnboardingGrid'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { IMCOnboardingsTableContext, MCOnboardingsTableContext } from './MCOnboardingsTable.context'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { useGridColumnPreferences } from 'src/utils/hooks/grid/useGridColumnPreferences'
import { getPartnerId } from 'src/selectors/user'
import { getSelectedMarket } from 'src/selectors/market'
import { MCOnboardingsHeader } from '../grid/MCOnboardingsHeader'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { MCOnboardingsContext } from '../mc-onboardings/MCOnboardings.context'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyMCOnboardings } from '../grid/EmptyMCOnboardings'
import { MCStatus } from 'src/constants/mc/enums'
import { MCOnboarding } from 'src/interfaces/mcOnboarding'
import { MCInviteDetailsModal } from 'src/components/mc-invite-details/MCInviteDetailsModal'
import { MCDetailsModal } from 'src/components/mc-details/MCDetailsModal'
import { MCDetails } from 'src/interfaces/mc'

interface FetchMCOnboardingsParams {
  limit?: number
  offset?: number
  selectedStatus: MCStatus
  searchQuery: string
  tagsFilter: string[]
  sortModel?: GridSortModel
}

interface MCOnboardingsTableProps {
  selectedStatus: MCStatus
}

const getRowId = (row: MCOnboarding) => (row.inviteId || row.masterContractorId) as string

const LOCKED_COLUMNS = ['id']

const MCOnboardingsTableComponent = ({ selectedStatus }: MCOnboardingsTableProps) => {
  const dispatch = useDispatch()

  const partnerId = useSelector(getPartnerId)
  const selectedMarket = useSelector(getSelectedMarket)

  const { searchQuery, tagsFilter } = useContext(MCOnboardingsContext)

  const { paginationModel, limit, offset, setPaginationModel } = useGridPagination(
    mcOnboardingPageSizeOptions,
  )

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [mcOnboardings, setOnboardings] = useState<Array<MCOnboarding>>([])
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
  const [onboardingToAction, setOnboardingToAction] = useState<MCOnboarding | null>(null)
  // can be inviteId or masterContractorId
  const [selectedMCOnboarding, setSelectedMCOnboarding] = useState<MCOnboarding | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mcOnboardingDetails, setMCOnboardingDetails] = useState<MCDetails | null>(null)
  const [counts, setCounts] = useState<number>(0)
  const [sortModel, setSortModel] = useState<GridSortModel>(
    mcOnboardingInitialSortFields[selectedStatus]
      ? [
          {
            field: mcOnboardingInitialSortFields[selectedStatus] || 'invitedAt',
            sort: 'desc',
          },
        ]
      : [],
  )

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const fetchOnboardings = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      tagsFilter,
      sortModel,
      selectedStatus,
    }: FetchMCOnboardingsParams) => {
      if (!partnerId) {
        return
      }

      setIsLoading(true)

      try {
        const params: any = {
          partnerId,
          limit,
          offset,
          status: selectedStatus,
        }

        if (searchQuery) {
          params.search = searchQuery
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0].field}:${sortModel[0].sort?.toUpperCase()}`
        }

        const payload: { data: Array<MCOnboarding>; pagination: Pagination } =
          await createAsyncAction(dispatch, FetchMCOnboardings.request(params))

        setOnboardings(payload.data)
        setCounts(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [partnerId, selectedMarket?.id],
  )

  const fetchOnboardingCounts = useCallback(() => {
    if (!partnerId) {
      return
    }

    const params: any = { partnerId }

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    dispatch(FetchMCOnboardingsCounts.request(params))
  }, [partnerId, selectedMarket?.id])

  const refreshOnboardings = useCallback(async () => {
    await fetchOnboardings({
      limit,
      offset,
      searchQuery,
      tagsFilter,
      sortModel,
      selectedStatus,
    })
    fetchOnboardingCounts()
  }, [
    limit,
    offset,
    searchQuery,
    tagsFilter,
    sortModel,
    selectedStatus,
    fetchOnboardings,
    fetchOnboardingCounts,
  ])

  useEffect(() => {
    fetchOnboardings({
      limit,
      offset,
      searchQuery,
      tagsFilter,
      sortModel,
      selectedStatus,
    })
  }, [
    limit,
    offset,
    partnerId,
    selectedMarket?.id,
    searchQuery,
    tagsFilter,
    sortModel,
    selectedStatus,
  ])

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const openOnboardingsItemAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, onboardingToEdit: MCOnboarding) => {
      setActionAnchor(event.currentTarget)
      setOnboardingToAction(onboardingToEdit)
    },
    [],
  )

  const closeOnboardingsItemAction = useCallback(() => {
    setActionAnchor(null)
    setOnboardingToAction(null)
  }, [])

  const mcOnboardingsContextValue = useMemo(() => {
    return {
      refreshOnboardings,

      actionAnchor,
      onboardingToAction,
      openOnboardingsItemAction,
      closeOnboardingsItemAction,
    }
  }, [
    refreshOnboardings,

    actionAnchor,
    onboardingToAction,
    openOnboardingsItemAction,
    closeOnboardingsItemAction,
  ])

  const handleRowClick = useCallback(({ row }: { row: MCOnboarding }) => {
    setSelectedMCOnboarding(row)
  }, [])

  const mcOnboardingDetailsRequestRef = useRef(0)

  const handleCloseDetails = useCallback(() => {
    mcOnboardingDetailsRequestRef.current += 1
    setSelectedMCOnboarding(null)
  }, [])

  const fetchContractorDetails = useCallback(async (masterContractorId: string) => {
    const requestId = ++mcOnboardingDetailsRequestRef.current

    try {
      const contractor: MCDetails = await createAsyncAction(
        dispatch,
        FetchMC.request({ masterContractorId }),
      )

      if (mcOnboardingDetailsRequestRef.current === requestId) {
        setMCOnboardingDetails(contractor)
      }
    } catch (err) {
      if (mcOnboardingDetailsRequestRef.current === requestId) {
        handleApiError(err)
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedMCOnboarding?.masterContractorId) {
      mcOnboardingDetailsRequestRef.current += 1
      setMCOnboardingDetails(null)
    } else {
      fetchContractorDetails(selectedMCOnboarding?.masterContractorId)
    }
  }, [selectedMCOnboarding?.masterContractorId])

  const closeMCDetails = useCallback(() => {
    mcOnboardingDetailsRequestRef.current += 1
    setMCOnboardingDetails(null)
    setSelectedMCOnboarding(null)
  }, [])

  const refreshOnboardingDetails = useCallback(async () => {
    if (selectedMCOnboarding?.masterContractorId) {
      await fetchContractorDetails(selectedMCOnboarding?.masterContractorId)
    }

    refreshOnboardings()
  }, [selectedMCOnboarding?.masterContractorId, refreshOnboardings])

  const { hasPermission } = useCheckPermissions()

  const columns = useMemo(() => {
    const cols = mcOnboardingColumns[selectedStatus]

    if (hasPermission(UserPermission.TagsView)) {
      return cols
    }

    return cols.filter((col) => col.field !== 'tags')
  }, [selectedStatus, hasPermission])

  const {
    columnVisibilityModel,
    pinnedColumns,
    orderedColumns,
    onColumnVisibilityModelChange,
    onPinnedColumnsChange,
    onColumnOrderChange,
    onToggleColumnVisibility,
    onPinColumn,
    onUnpinColumn,
  } = useGridColumnPreferences({
    tableKey: `mc-onboardings-${selectedStatus}`,
    columns,
    lockedColumns: LOCKED_COLUMNS,
  })

  const contextValue = useMemo<IMCOnboardingsTableContext>(() => {
    return {
      ...mcOnboardingsContextValue,
      columnsConfig: columns,
      columnVisibilityModel,
      pinnedColumns,
      lockedColumns: LOCKED_COLUMNS,
      onToggleColumnVisibility,
      onColumnVisibilityModelChange,
      onPinColumn,
      onUnpinColumn,
    }
  }, [
    mcOnboardingsContextValue,
    columns,
    columnVisibilityModel,
    pinnedColumns,
    onToggleColumnVisibility,
    onColumnVisibilityModelChange,
    onPinColumn,
    onUnpinColumn,
  ])

  return (
    <MCOnboardingsTableContext.Provider value={contextValue}>
      <MCOnboardingsHeader />
      <GridTable<MCOnboarding>
        rows={mcOnboardings}
        columns={orderedColumns}
        isLoading={isLoading}
        paginationModel={paginationModel}
        pageSizeOptions={mcOnboardingPageSizeOptions}
        rowCount={counts}
        sortModel={sortModel}
        columnVisibilityModel={columnVisibilityModel}
        pinnedColumns={pinnedColumns}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onPinnedColumnsChange={onPinnedColumnsChange}
        onColumnOrderChange={onColumnOrderChange}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        EmptyElement={EmptyMCOnboardings}
        tableDataTestId="master-contractors-onboarding-list-table"
        rowDataTestIdPrefix="master-contractors-onboarding-list"
      />
      {selectedMCOnboarding && !selectedMCOnboarding.isSignedUp && (
        <MCInviteDetailsModal
          mcOnboarding={selectedMCOnboarding}
          onClose={handleCloseDetails}
          onRefresh={refreshOnboardings}
        />
      )}
      {mcOnboardingDetails && (
        <MCDetailsModal
          mcDetails={mcOnboardingDetails}
          onClose={closeMCDetails}
          onRefresh={refreshOnboardingDetails}
        />
      )}
    </MCOnboardingsTableContext.Provider>
  )
}

export const MCOnboardingsTable = memo(MCOnboardingsTableComponent)
